'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, MapPin, DollarSign, Users, Trophy, ChevronRight, Filter, Search, X, ArrowLeft, Timer } from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams, useRouter } from 'next/navigation';

interface RaceEvent {
  id: string;
  name: string;
  date: string;
  venue: string;
  city: string;
  format: string[];
  fee: number;
  status: 'upcoming' | 'live' | 'completed';
  description: string;
  round: number | null;
  season: number;
  total_entrants: number | null;
  winner: string | null;
  winner_time: string | null;
  organizer_name?: string;
  organizer_id?: string;
  registration_open?: boolean;
}

type Tab = 'upcoming' | 'past';
type SeasonFilter = 'all' | string;

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
}

function UpcomingEventCard({ event }: { event: RaceEvent }) {
  const { user } = useAuth();
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-all duration-200 hover:-translate-y-0.5 group flex flex-col">
      <div className="h-1.5 w-full bg-gradient-to-r from-primary to-accent" />
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-4">
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            {event.round ? `Round ${event.round} · ` : ''}{event.season} Season
          </span>
          <span className="px-2 py-0.5 rounded text-xs font-bold badge-upcoming shrink-0 ml-2">UPCOMING</span>
        </div>
        <Link href={`/events?event=${event.id}`} className="block mb-1">
          <h3 className="text-lg font-bold text-foreground leading-snug mb-1 group-hover:text-primary transition-colors">{event.name}</h3>
        </Link>
        {event.organizer_name && (
          <Link
            href={event.organizer_id ? `/organizer-profile/${event.organizer_id}` : `/events?organizer=${encodeURIComponent(event.organizer_name)}`}
            className="text-xs font-semibold text-primary/80 hover:text-primary mb-3 block transition-colors"
            onClick={e => e.stopPropagation()}
          >
            by {event.organizer_name}
          </Link>
        )}
        <p className="text-sm text-muted-foreground leading-relaxed mb-5">{event.description}</p>
        <div className="space-y-2.5 mb-5">
          <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
            <Calendar size={15} className="text-primary shrink-0" />
            <span className="font-medium text-foreground">{formatDate(event.date)}</span>
          </div>
          <div className="flex items-start gap-2.5 text-sm text-muted-foreground">
            <MapPin size={15} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">{event.venue}</p>
              <p>{event.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 text-sm text-muted-foreground">
            <DollarSign size={15} className="text-primary shrink-0" />
            <span>Entry: <span className="font-bold text-foreground">RM {event.fee}</span></span>
          </div>
        </div>
        {event.format?.length > 0 && (
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Race Format</p>
            <div className="flex flex-wrap gap-1.5">
              {event.format.map((step, i) => (
                <span key={`format-${event.id}-${i}`} className="px-2 py-1 rounded text-xs font-semibold bg-muted/50 text-muted-foreground border border-border">{step}</span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-auto">
          {event.registration_open === false ? (
            <div className="w-full text-center py-2.5 text-sm font-bold uppercase tracking-wide rounded-md bg-muted/30 border border-border text-muted-foreground cursor-not-allowed">
              Registration Closed
            </div>
          ) : user ? (
            <Link href={`/event-registration?event=${event.id}`} className="flex-1 block text-center py-2.5 text-sm font-bold uppercase tracking-wide rounded-md btn-primary">
              Register — RM {event.fee}
            </Link>
          ) : (
            <Link href="/login" className="flex-1 block text-center py-2.5 text-sm font-bold uppercase tracking-wide rounded-md btn-primary">
              Sign In to Register
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function PastEventCard({ event }: { event: RaceEvent }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:border-border/80 transition-all duration-200 group flex flex-col">
      <div className="h-1 w-full bg-gradient-to-r from-muted-foreground/30 to-muted-foreground/10" />
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-4">
          <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{event.season} Season</span>
          <span className="px-2 py-0.5 rounded text-xs font-bold badge-completed shrink-0 ml-2">COMPLETED</span>
        </div>
        <Link href={`/events?event=${event.id}`} className="block mb-1">
          <h3 className="text-base font-bold text-foreground leading-snug mb-1 group-hover:text-primary transition-colors">{event.name}</h3>
        </Link>
        {event.organizer_name && (
          <Link
            href={event.organizer_id ? `/organizer-profile/${event.organizer_id}` : `/events?organizer=${encodeURIComponent(event.organizer_name)}`}
            className="text-xs font-semibold text-primary/80 hover:text-primary mb-2 block transition-colors"
            onClick={e => e.stopPropagation()}
          >
            by {event.organizer_name}
          </Link>
        )}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar size={14} className="text-muted-foreground shrink-0" />
            {formatDate(event.date)}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={14} className="text-muted-foreground shrink-0" />
            <span className="truncate">{event.city}</span>
          </div>
          {event.total_entrants && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users size={14} className="text-muted-foreground shrink-0" />
              {event.total_entrants} drivers entered
            </div>
          )}
        </div>
        {event.winner && (
          <div className="bg-gold/8 border border-gold/20 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-gold shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gold/80">Winner</p>
                <p className="text-sm font-bold text-foreground">{event.winner}</p>
                {event.winner_time && <p className="text-xs font-tabular text-muted-foreground">{event.winner_time}</p>}
              </div>
            </div>
          </div>
        )}
        <div className="mt-auto">
          <Link href={`/live-results?event=${event.id}`} className="flex items-center justify-center gap-2 w-full py-2.5 text-sm font-bold uppercase tracking-wide rounded-md btn-secondary">
            View Results <ChevronRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// Single event detail view
function SingleEventView({ event, onBack }: { event: RaceEvent; onBack: () => void }) {
  const { user } = useAuth();
  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft size={16} /> Back to All Events
      </button>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-primary to-accent" />
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {event.round ? `Round ${event.round} · ` : ''}{event.season} Season
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-bold shrink-0 ml-2 ${event.status === 'upcoming' ? 'badge-upcoming' : event.status === 'live' ? 'badge-live' : 'badge-completed'}`}>
              {event.status.toUpperCase()}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-foreground leading-snug mb-2">{event.name}</h2>
          {event.organizer_name && (
            <Link
              href={event.organizer_id ? `/organizer-profile/${event.organizer_id}` : `/events?organizer=${encodeURIComponent(event.organizer_name)}`}
              className="text-sm font-semibold text-primary/80 hover:text-primary mb-4 block transition-colors"
            >
              Organized by {event.organizer_name}
            </Link>
          )}
          {event.description && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{event.description}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Calendar size={16} className="text-primary shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</p>
                <p className="font-semibold text-foreground">{formatDate(event.date)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <MapPin size={16} className="text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Venue</p>
                <p className="font-semibold text-foreground">{event.venue}</p>
                <p>{event.city}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <DollarSign size={16} className="text-primary shrink-0" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Entry Fee</p>
                <p className="font-semibold text-foreground">RM {event.fee}</p>
              </div>
            </div>
          </div>
          {event.format?.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Race Format</p>
              <div className="flex flex-wrap gap-1.5">
                {event.format.map((step, i) => (
                  <span key={i} className="px-2 py-1 rounded text-xs font-semibold bg-muted/50 text-muted-foreground border border-border">{step}</span>
                ))}
              </div>
            </div>
          )}
          {event.status === 'upcoming' && (
            event.registration_open === false ? (
              <div className="w-full text-center py-3 text-sm font-bold uppercase tracking-wide rounded-md bg-muted/30 border border-border text-muted-foreground cursor-not-allowed">
                Registration Closed
              </div>
            ) : user ? (
              <Link href={`/event-registration?event=${event.id}`} className="block text-center py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary">
                Register Now — RM {event.fee}
              </Link>
            ) : (
              <Link href="/login" className="block text-center py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary">
                Sign In to Register
              </Link>
            )
          )}
          {event.status === 'completed' && (
            <Link href={`/live-results?event=${event.id}`} className="flex items-center justify-center gap-2 w-full py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-secondary">
              View Results <ChevronRight size={15} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EventsPageClient() {
  const [activeTab, setActiveTab] = useState<Tab>('upcoming');
  const [seasonFilter, setSeasonFilter] = useState<SeasonFilter>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const searchParams = useSearchParams();
  const router = useRouter();

  // URL param: specific event or organizer filter
  const eventIdParam = searchParams.get('event');
  const organizerParam = searchParams.get('organizer');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('race_events').select('*').order('date', { ascending: true });
    if (data) setEvents(data as any);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchEvents();

    // Real-time subscription
    const channel = supabase
      .channel('events-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'race_events' }, () => {
        fetchEvents();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchEvents]);

  // If a specific event is requested, show single event view
  const singleEvent = eventIdParam ? events.find(e => e.id === eventIdParam) : null;

  // If organizer filter is active, filter all events by organizer
  const organizerFilteredEvents = organizerParam
    ? events.filter(e => e.organizer_name === organizerParam)
    : events;

  const upcomingEvents = organizerFilteredEvents.filter(e => e.status === 'upcoming' || e.status === 'live');
  const completedEvents = organizerFilteredEvents.filter(e => e.status === 'completed');
  const seasons = [...new Set(completedEvents.map(e => String(e.season)))].sort((a, b) => Number(b) - Number(a));
  const cities = [...new Set(organizerFilteredEvents.map(e => e.city).filter(Boolean))].sort();

  const filteredUpcoming = upcomingEvents.filter(e => {
    const matchesSearch = !searchQuery || e.name.toLowerCase().includes(searchQuery.toLowerCase()) || e.city.toLowerCase().includes(searchQuery.toLowerCase()) || e.venue.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = cityFilter === 'all' || e.city === cityFilter;
    return matchesSearch && matchesCity;
  });

  const filteredPast = completedEvents.filter(e => {
    const matchesSeason = seasonFilter === 'all' || String(e.season) === seasonFilter;
    const matchesSearch = !searchQuery || e.name.toLowerCase().includes(searchQuery.toLowerCase()) || e.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = cityFilter === 'all' || e.city === cityFilter;
    return matchesSeason && matchesSearch && matchesCity;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setSeasonFilter('all');
    setCityFilter('all');
  };

  const hasActiveFilters = searchQuery || seasonFilter !== 'all' || cityFilter !== 'all';

  // Single event view
  if (singleEvent) {
    return (
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">SpeedPass</p>
          <h1 className="text-4xl md:text-5xl font-extrabold uppercase tracking-tight text-foreground mb-3">Event Details</h1>
        </div>
        <SingleEventView event={singleEvent} onBack={() => router.push('/events')} />
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">SpeedPass</p>
        <h1 className="text-4xl md:text-5xl font-extrabold uppercase tracking-tight text-foreground mb-3">Race Events</h1>
        {organizerParam ? (
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground text-base">Showing events by <span className="font-bold text-foreground">{organizerParam}</span></p>
            <button
              onClick={() => router.push('/events')}
              className="flex items-center gap-1 text-xs font-bold text-negative border border-negative/30 hover:bg-negative/10 px-2 py-1 rounded transition-colors"
            >
              <X size={12} /> Clear
            </button>
          </div>
        ) : (
          <p className="text-muted-foreground text-base max-w-xl">Register for upcoming championship rounds or review past race results and standings.</p>
        )}
      </div>

      {/* Time Attack Banner */}
      <div className="mb-8 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/30 rounded-xl p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
            <Timer size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-0.5">Open Category</p>
            <h3 className="text-base font-extrabold text-foreground">Time Attack</h3>
            <p className="text-xs text-muted-foreground">No registration or payment required. Submit your best lap time and compete on the leaderboard.</p>
          </div>
        </div>
        <Link href="/time-attack" className="shrink-0 flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wide rounded-md btn-primary whitespace-nowrap">
          <Timer size={14} />View Leaderboard
        </Link>
      </div>

      {/* Search bar */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search events by name, venue, or city..."
          className="w-full pl-10 pr-10 py-2.5 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <X size={15} />
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex rounded-lg border border-border overflow-hidden w-fit">
          {(['upcoming', 'past'] as Tab[]).map(tab => (
            <button key={`tab-${tab}`} onClick={() => setActiveTab(tab)} className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-all duration-150 ${activeTab === tab ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
              {tab === 'upcoming' ? `Upcoming (${upcomingEvents.length})` : `Past Events (${completedEvents.length})`}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* City filter */}
          {cities.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-muted-foreground" />
              <select
                value={cityFilter}
                onChange={e => setCityFilter(e.target.value)}
                className="px-3 py-1.5 rounded-md bg-input border border-border text-xs font-bold text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="all">All Cities</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {/* Season filter (past tab) */}
          {activeTab === 'past' && seasons.length > 0 && (
            <div className="flex items-center gap-1">
              {(['all', ...seasons] as SeasonFilter[]).map(s => (
                <button key={`season-${s}`} onClick={() => setSeasonFilter(s)} className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide transition-all duration-150 ${seasonFilter === s ? 'bg-primary/20 text-primary border border-primary/40' : 'text-muted-foreground border border-border hover:border-primary/30 hover:text-foreground'}`}>
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
          )}

          {hasActiveFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-bold text-negative border border-negative/30 hover:bg-negative/10 transition-colors">
              <X size={12} />Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : activeTab === 'upcoming' ? (
        <div>
          {filteredUpcoming.length === 0 ? (
            <div className="text-center py-24 bg-card border border-border rounded-xl">
              <Calendar size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">{hasActiveFilters || organizerParam ? 'No Events Match Your Filters' : 'No Upcoming Events'}</h3>
              <p className="text-sm text-muted-foreground">{hasActiveFilters || organizerParam ? 'Try adjusting your search or filters.' : 'The race calendar is being finalized. Check back soon.'}</p>
              {(hasActiveFilters || organizerParam) && <button onClick={() => { clearFilters(); router.push('/events'); }} className="mt-4 px-4 py-2 text-sm font-bold rounded-md btn-secondary">Clear Filters</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredUpcoming.map(event => <UpcomingEventCard key={event.id} event={event} />)}
            </div>
          )}
        </div>
      ) : (
        <div>
          {filteredPast.length === 0 ? (
            <div className="text-center py-24 bg-card border border-border rounded-xl">
              <Trophy size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">{hasActiveFilters || organizerParam ? 'No Events Match Your Filters' : 'No Past Events'}</h3>
              <p className="text-sm text-muted-foreground">{hasActiveFilters || organizerParam ? 'Try adjusting your search or filters.' : 'Past race results will appear here.'}</p>
              {(hasActiveFilters || organizerParam) && <button onClick={() => { clearFilters(); router.push('/events'); }} className="mt-4 px-4 py-2 text-sm font-bold rounded-md btn-secondary">Clear Filters</button>}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredPast.map(event => <PastEventCard key={event.id} event={event} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}